import React, {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
  PropsWithChildren
} from 'react';
import styled from 'styled-components';
import { LogoAnimationLoader } from 'src/components/LogoLoader';

export const PrefillContext = createContext<{
  isLoading: boolean;
  isCalled: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsCalled: Dispatch<SetStateAction<boolean>>;
}>({
  isLoading: false,
  isCalled: false,
  setIsLoading: () => undefined,
  setIsCalled: () => undefined,
});

export const PrefillContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCalled, setIsCalled] = useState(false);

  const value = useMemo(
    () => ({
      isLoading,
      isCalled,
      setIsLoading,
      setIsCalled,
    }),
    [isCalled, isLoading],
  );

  return (
    <PrefillContext.Provider value={value}>
      {isLoading && <LoadingPrefill />}
      {children}
    </PrefillContext.Provider>
  );
};

const LoadingPrefill = () => (
  <Wrapper>
    <LogoAnimationLoader autoFill />
  </Wrapper>
);

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  z-index: 1000000;
`;

export const usePrefillContext = () => {
  const context = useContext(PrefillContext);
  if (context === undefined) {
    throw new Error(
      'usePrefillContext must be used within a PrefillContextProvider',
    );
  }
  return context;
};
